// Google is the only way in (no passwords); the server decides who may enter.
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import type { Meta } from "../../lib/types";
import { Logo } from "../../ui/Icon";

// ?sso_error= from the callback: kept across re-renders until the next sign-in attempt
let ssoErr: string | null = null;
function takeSsoError() {
  const e = new URLSearchParams(location.search).get("sso_error");
  if (e) { ssoErr = e; history.replaceState(null, "", location.pathname + location.hash); }
  return ssoErr;
}

export function Login() {
  const [msg] = useState(takeSsoError);
  const [busy, setBusy] = useState(false);
  // its own request: here a failure means "can't reach Compass", not "modules unknown"
  const meta = useQuery({ queryKey: ["/api/meta", "login"], queryFn: () => api<Meta>("/api/meta"), retry: 1 });
  // back from Google restores this page from the bfcache with the button still "Redirecting…"
  useEffect(() => {
    const onShow = (e: PageTransitionEvent) => { if (e.persisted) setBusy(false); };
    addEventListener("pageshow", onShow);
    return () => removeEventListener("pageshow", onShow);
  }, []);
  // the button renders optimistically (Google is almost always configured); meta only swaps it out when it isn't
  const unset = meta.isSuccess && !meta.data?.sso?.google;
  return (
    <div className="login">
      <div className="card">
        <div className="login-mark"><Logo /></div>
        <h1>Sign in to Compass</h1><p className="muted">StampMyVisa sales CRM</p>
        {msg && <div className="banner red" role="alert">{msg}</div>}
        {meta.isError ? <div className="banner red" role="alert">Can't reach Compass. Check your connection and reload.</div>
          : unset ? <div className="banner quiet">Google sign-in isn't set up on this server yet (WORKOS_CLIENT_ID / WORKOS_API_KEY). Ask an admin.</div>
          : <>
            <a className={`gbtn ${busy ? "busy" : ""}`} href="/api/auth/sso/google" onClick={() => { ssoErr = null; setBusy(true); }}>
              <GoogleG /><span>{busy ? "Redirecting to Google…" : "Sign in with Google"}</span>
            </a>
            <p className="muted small login-hint">Use your work Google account</p>
          </>}
      </div>
      <p className="muted small login-foot">Access is managed by your Compass admin.</p>
    </div>
  );
}

const GoogleG = () => (
  <svg viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);
